import { DrawingProperties, parsePinPropertyKey, SelectionMode } from '@cadstrom/renderer';
import { useMatchRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { ModelModuleChip } from '#components/model-module-chip/model-module-chip.js';
import { NetChip } from '#components/net-chip/net-chip.js';
import { useModuleFilterContext } from '#src/routes/results/analysis-results-page/context/module-filter-context.js';
import { createModuleFilterMenuItem } from '#src/routes/results/analysis-results-page/utils/create-module-filter-menu-item.js';
import { useActiveDesignContext } from '../context/active-design-context.js';
import { useCoreModelContext } from '../context/core-model-context.js';
import { useDrawingManagerContext } from '../context/drawing-manager-context.js';
import { useInteractionManagerContext } from '../context/interaction-manager-context.js';
import { formatPositionForGrid } from './grid-utils.js';

import type { DrawingPropertyValue, ISelection } from '@cadstrom/renderer';
import type { ModelGrid } from '@cadstrom/sigma-engine-shared';

export type SelectionViewProps = {
	modelGrid: ModelGrid;
};

export function SelectionView({ modelGrid }: SelectionViewProps) {
	const { coreModel } = useCoreModelContext();
	const { activeDesign } = useActiveDesignContext();
	const { interactionManager } = useInteractionManagerContext();
	const { drawingManager } = useDrawingManagerContext();
	const matchRoute = useMatchRoute();
	const [selectionType, setSelectionType] = useState<DrawingProperties | 'multiple' | 'rect'>();
	const [selection, setSelection] = useState<DrawingPropertyValue[]>([]);

	// Check if we're on the analysis results page and get the filter handler from context
	const isOnResultsPage = Boolean(
		matchRoute({ to: '/orgs/$orgName/projects/$projectId/revisions/$revisionId/results' }),
	);
	const moduleFilterContext = useModuleFilterContext();
	const handleAddModuleFilter = moduleFilterContext?.handleAddModuleFilter;

	useEffect(() => {
		function updateSelection() {
			const selection = interactionManager.getCurrentSelection();
			if (!selection) {
				setSelectionType(undefined);
				setSelection([]);
				return;
			}

			if (selection.size > 1) {
				setSelectionType(DrawingProperties.ObjectId);
				setSelection([]);
			} else {
				const key = Array.from(selection.keys())[0]!;
				setSelectionType(key);
				setSelection(Array.from(selection.get(key)!));
			}
		}

		updateSelection();

		interactionManager.on('clearSelection', updateSelection);
		interactionManager.on('selectElementsByProperty', updateSelection);

		return () => {
			interactionManager.removeListener('clearSelection', updateSelection);
			interactionManager.removeListener('selectElementsByProperty', updateSelection);
		};
	}, [interactionManager]);

	useEffect(() => {
		if (!drawingManager) {
			return;
		}

		function selectionChanged(selection: ISelection) {
			if (selection.isEmpty) {
				setSelectionType(undefined);
				setSelection([]);
			} else if (selection.mode === SelectionMode.Rectangular) {
				const coords = selection.selectionRect!.drawingCoords();
				setSelectionType('rect');
				setSelection([coords.x1, coords.y1, coords.x2, coords.y2]);
			}
		}

		drawingManager.on('selectionChanged', selectionChanged);
		return () => {
			drawingManager.removeListener('selectionChanged', selectionChanged);
		};
	}, [drawingManager]);

	if (selectionType === undefined) {
		return <div />; // Empty div to maintain the space in the grid
	}

	return (
		<div className='flex items-center justify-end gap-1'>
			<p className='text-sm text-gray-a11'>
				{(() => {
					switch (selectionType) {
						case DrawingProperties.Net: {
							return selection.length > 1 ? 'Selected nets:' : 'Selected net:';
						}
						case DrawingProperties.Component: {
							return selection.length > 1 ? 'Selected components:' : 'Selected component:';
						}
						case DrawingProperties.ObjectId: {
							return selection.length > 1 ? 'Selected objects:' : 'Selected object:';
						}
						case DrawingProperties.Pin: {
							return selection.length > 1 ? 'Selected pins:' : 'Selected pin:';
						}
						case DrawingProperties.Pad: {
							return selection.length > 1 ? 'Selected pads:' : 'Selected pad:';
						}
						case 'rect': {
							return 'Selected rect from';
						}
						case 'multiple':
						default: {
							return null;
						}
					}
				})()}
			</p>
			{(() => {
				if (selection.length === 0) {
					return null;
				}

				switch (selectionType) {
					case 'multiple': {
						return 'Multiple objects selected';
					}

					case 'rect': {
						const start = formatPositionForGrid(
							{
								x: selection[0] as number,
								y: selection[1] as number,
							},
							modelGrid,
						);
						const end = formatPositionForGrid(
							{
								x: selection[2] as number,
								y: selection[3] as number,
							},
							modelGrid,
						);
						return (
							<p className='text-sm'>
								({start?.x ?? selection[0]}, {start?.y ?? selection[1]})&nbsp;to&nbsp;({end?.x ?? selection[2]},{' '}
								{end?.y ?? selection[3]})
							</p>
						);
					}

					case DrawingProperties.ObjectId: {
						return selection.map((s) => {
							const projectModule = coreModel.modules.find((m) => m.name === s);

							return (
								<ModelModuleChip
									key={projectModule?.name ?? String(s)}
									moduleReferenceDesignator={projectModule?.name ?? String(s)}
									selected
									xToDeselect
									renderAdditionalMenuItems={
										isOnResultsPage && handleAddModuleFilter ?
											createModuleFilterMenuItem(projectModule?.name ?? String(s), handleAddModuleFilter)
										:	undefined
									}
								/>
							);
						});
					}

					case DrawingProperties.Net: {
						return selection.map((s) => {
							return <NetChip key={s as string} net={s as string} selected xToDeselect />;
						});
					}

					case DrawingProperties.Pin:
					case DrawingProperties.Pad: {
						// Selection value is the compound key produced by pinPropertyKey — parse it to recover
						// the module ref and pin/pad ref.
						return selection.map((s) => {
							const parsed = parsePinPropertyKey(s as string);
							if (!parsed) {
								return null;
							}
							const { moduleRef, pinRef } = parsed;
							const module = activeDesign.modules.find((m) => m.ref === moduleRef);
							if (!module) {
								return null;
							}
							return (
								<ModelModuleChip
									key={s as string}
									moduleReferenceDesignator={module.name}
									symbolPin={selectionType === DrawingProperties.Pin && pinRef ? pinRef : undefined}
									footprintPad={selectionType === DrawingProperties.Pad && pinRef ? pinRef : undefined}
									selected
									xToDeselect
								/>
							);
						});
					}

					default: {
						return <p className='text-sm'>{selection.join(', ')}</p>;
					}
				}
			})()}
		</div>
	);
}
